import {
  Building2,
  Phone,
  Navigation,
  Globe,
  MapPin,
  Star,
  Sparkles,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AI_UI_STATES } from '../../constants/aiSearchIntents';
import { UI_STATE_COLORS } from './uiStateColors';
import { selectEffectiveCoordinates } from '../../slices/locationSlice';
import { getDistanceUnitForCoordinates } from '../../utils/distanceUnit';
import {
  formatDistance,
  buildDirectionsUrl,
  normalizePhone,
} from './placeHelpers';

const PlaceCard = ({ place, theme, isRecommended, distanceUnit }) => {
  const { t } = useTranslation();
  const distance = formatDistance(place, distanceUnit);
  const directionsUrl = buildDirectionsUrl(place);
  const telHref = place.phone ? `tel:${normalizePhone(place.phone)}` : null;

  const ctaBaseClasses =
    'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold min-h-[44px] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1';

  return (
    <li className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <Building2
            className="w-5 h-5 text-gray-500"
            aria-hidden="true"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">
              {place.name}
            </h4>
            {isRecommended ? (
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${theme.iconBg} ${theme.iconText} flex-shrink-0`}
                title={t('ai_summary_highlighted_place', 'The AI summary singled out this place')}
              >
                <Sparkles className="w-3 h-3" aria-hidden="true" />
                AI Pick
              </span>
            ) : null}
          </div>

          {(distance || typeof place.rating === 'number') && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {distance ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  <Navigation className="w-3 h-3" aria-hidden="true" />
                  {distance}
                </span>
              ) : null}
              {typeof place.rating === 'number' ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">
                  <Star
                    className="w-3 h-3 fill-current"
                    aria-hidden="true"
                  />
                  {place.rating.toFixed(1)}
                  {typeof place.userRatingCount === 'number'
                    ? ` (${place.userRatingCount})`
                    : ''}
                </span>
              ) : null}
            </div>
          )}

          {place.address ? (
            <p className="flex items-start gap-1.5 mt-2 text-xs text-gray-600">
              <MapPin
                className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400"
                aria-hidden="true"
              />
              <span className="line-clamp-2">{place.address}</span>
            </p>
          ) : place.type ? (
            <p className="text-xs text-gray-500 mt-1 capitalize">
              {place.type}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 mt-3">
            {telHref ? (
              <a
                href={telHref}
                className={`${ctaBaseClasses} text-white ${theme.buttonBg} ${theme.buttonHover} focus:ring-primary/40`}
                aria-label={`Call ${place.name}`}
              >
                <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                Call
              </a>
            ) : null}

            {directionsUrl ? (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`${ctaBaseClasses} bg-white text-gray-700 border border-gray-200 hover:border-gray-300 focus:ring-gray-300`}
                aria-label={`Get directions to ${place.name}`}
              >
                <Navigation className="w-3.5 h-3.5" aria-hidden="true" />
                Directions
              </a>
            ) : null}

            {place.website ? (
              <a
                href={place.website}
                target="_blank"
                rel="noopener noreferrer"
                className={`${ctaBaseClasses} bg-white text-gray-700 border border-gray-200 hover:border-gray-300 focus:ring-gray-300`}
                aria-label={`Visit website for ${place.name}`}
              >
                <Globe className="w-3.5 h-3.5" aria-hidden="true" />
                Website
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
};

const PlacesCards = ({ places, uiState, recommendedPlaceId }) => {
  const { t } = useTranslation();
  const list = (Array.isArray(places) ? places : []).slice(0, 5);
  const theme =
    UI_STATE_COLORS[uiState] || UI_STATE_COLORS[AI_UI_STATES.NORMAL];
  const coordinates = useSelector(selectEffectiveCoordinates);
  const distanceUnit = getDistanceUnitForCoordinates(coordinates);

  if (list.length === 0) return null;

  return (
    <section aria-label={t('nearby_places', 'Nearby places')}>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
          {t('nearby_places_heading', 'Nearby Places ({{count}})', { count: list.length })}
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5">{t('powered_by_google', 'Powered by Google Places API')}</p>
      </div>

      <ul className="space-y-3">
        {list.map((place) => (
          <PlaceCard
            key={place.id}
            place={place}
            theme={theme}
            isRecommended={
              !!recommendedPlaceId && place.id === recommendedPlaceId
            }
            distanceUnit={distanceUnit}
          />
        ))}
      </ul>
    </section>
  );
};

export default PlacesCards;
